import * as React from 'react';

const PLACEHOLDER = '/img/hotel-image-placeholder.png';

type Props = {
  image?: string | null;
  /** Small chip over the top-left corner of the image (type, category). */
  badge?: React.ReactNode;
  title: React.ReactNode;
  /** Link on the title, opened in a new tab. */
  href?: string;
  /** Location line under the title. */
  subtitle?: React.ReactNode;
  /** Short facts (stars, duration), separated by dots. */
  facts?: React.ReactNode[];
  /** Red notice above the footer (unavailable, minimum stay). */
  warning?: React.ReactNode;
  /** Bottom left: usually a price. */
  price?: React.ReactNode;
  /** Bottom right: usually a KoobButton size="sm". */
  action?: React.ReactNode;
};

/**
 * The one popup look for every KOOB map: image, title, location, facts,
 * price and action. Callers give data, not layout.
 */
export function KoobMapCard({
  image,
  badge,
  title,
  href,
  subtitle,
  facts = [],
  warning,
  price,
  action,
}: Props) {
  const shownFacts = facts.filter(
    (fact) => fact !== null && fact !== undefined && fact !== false,
  );

  return (
    // On a short map the image gives up its height first, then the details
    // scroll; the price and action always stay in view.
    <div className="flex min-h-0 w-[272px] max-w-full flex-col overflow-hidden bg-white text-left font-sans">
      <div className="relative h-36 min-h-0 shrink-[100] bg-gray-100">
        <img
          className="block h-full w-full object-cover"
          src={image || PLACEHOLDER}
          onError={(event) => {
            event.currentTarget.src = PLACEHOLDER;
          }}
          alt=""
        />
        {badge && (
          <div className="absolute left-2.5 top-2.5 flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-xs font-medium text-gray-800 shadow-sm">
            {badge}
          </div>
        )}
      </div>

      <div
        className={`flex min-h-0 flex-col gap-1 overflow-y-auto px-3.5 pt-3.5 ${
          price || action ? '' : 'pb-3.5'
        }`}
      >
        <div className="line-clamp-2 text-[15px] font-semibold leading-snug text-gray-900">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="text-inherit no-underline hover:underline"
            >
              {title}
            </a>
          ) : (
            title
          )}
        </div>

        {subtitle && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <i className="fa-light fa-location-dot" />
            <span className="truncate">{subtitle}</span>
          </div>
        )}

        {shownFacts.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
            {shownFacts.map((fact, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="text-gray-300">•</span>}
                <span className="flex items-center gap-1">{fact}</span>
              </React.Fragment>
            ))}
          </div>
        )}

        {warning && (
          <div className="text-xs font-medium text-red-600">{warning}</div>
        )}
      </div>

      {(price || action) && (
        <div className="mx-3.5 mb-3.5 mt-3 flex shrink-0 items-center justify-between gap-3 border-0 border-t border-solid border-gray-100 pt-2.5">
          <div className="min-w-0 text-sm">{price}</div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
    </div>
  );
}

/** Hotel stars as a fact for KoobMapCard. */
export function KoobMapStars({ count }: { count?: number | null }) {
  if (!count) {
    return null;
  }

  return (
    <span className="flex items-center gap-0.5 text-amber-400">
      {Array.from({ length: count }, (_, index) => (
        <i key={index} className="fa-solid fa-star text-[10px]" />
      ))}
    </span>
  );
}
